/**
 * Extracts the HTTP status from a Tactic API error.
 *
 * The client throws an `ApiClientError` for every non-ok response, so by the
 * time a caller sees one the status is the only thing that says whether trying
 * again could help: a 5xx or a dropped connection is worth a retry, a 4xx is
 * the server's settled answer about this request.
 *
 * Mirrors the shapes {@link getApiErrorCode} understands: an `ApiClientError`
 * (`error.response.status`), or a `ResponseWithStatus` passed directly.
 *
 * @param error - The error object from an API call or catch block
 * @returns The status, or `undefined` for an error that never reached HTTP
 *
 * @example
 * ```ts
 * // Retry a flaky connection, but not a rejected request.
 * retry: (failureCount, error) => {
 *   const status = getApiErrorStatus(error);
 *   if (status && status >= 400 && status < 500) return false;
 *   return failureCount < 2;
 * }
 * ```
 */
export function getApiErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const err = error as Record<string, unknown>;

  // ApiClientError: the thrown error carries the ResponseWithStatus.
  if (err.response && typeof err.response === "object") {
    const response = err.response as Record<string, unknown>;
    if (typeof response.status === "number") {
      return response.status;
    }
  }

  // A ResponseWithStatus passed directly.
  if (typeof err.status === "number") {
    return err.status;
  }

  return undefined;
}
